import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Paper as MenuPaper, List, ListItem, ListItemButton, ListItemAvatar, Avatar
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Pause as PauseIcon,
  PlayArrow as ResumeIcon, Circle as CircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import supportService from '../../../services/supportService';

const AdminSupportAgents = () => {
  const { t } = useTranslation(['support', 'common']);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await supportService.getAgents();
      if (res.success) setAgents(res.data || []);
    } catch {
      setError(t('support:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const res = await supportService.searchUsers(searchQuery);
        if (res.success) {
          setSearchResults(res.data || []);
          setShowResults(true);
        }
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleAddAgent = async () => {
    if (!selectedUser?.id) return;
    try {
      await supportService.createAgent({ userId: selectedUser.id });
      setSelectedUser(null);
      setDialogOpen(false);
      fetchAgents();
    } catch (err) {
      setError(err.response?.data?.message || t('support:errors.sendError'));
    }
  };

  const handleTogglePause = async (agentId) => {
    try {
      await supportService.toggleAgentPause(agentId);
      fetchAgents();
    } catch { setError(t('support:errors.sendError')); }
  };

  const handleRemove = async (agentId) => {
    if (!window.confirm(t('support:admin.agents.removeConfirm'))) return;
    try {
      await supportService.removeAgent(agentId);
      fetchAgents();
    } catch { setError(t('support:errors.sendError')); }
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setSearchQuery('');
    setShowResults(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setSearchQuery('');
    setShowResults(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>{t('support:admin.agents.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          {t('support:admin.agents.add')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {agents.length === 0 ? (
        <Alert severity="info">{t('support:admin.agents.noAgents')}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('support:admin.agents.title')}</TableCell>
                <TableCell>{t('support:admin.tickets.filterStatus')}</TableCell>
                <TableCell>{t('support:admin.agents.activeTickets')}</TableCell>
                <TableCell>{t('support:admin.agents.maxTickets')}</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map(agent => (
                <TableRow key={agent.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircleIcon sx={{ fontSize: 10, color: agent.isOnline ? 'success.main' : 'text.disabled' }} />
                      <Typography variant="body2">{agent.user?.name || agent.userId}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        agent.isPaused
                          ? t('support:admin.agents.paused')
                          : agent.isOnline
                            ? t('support:admin.agents.online')
                            : t('support:admin.agents.offline')
                      }
                      color={agent.isPaused ? 'warning' : agent.isOnline ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{agent.activeTicketCount || 0}</TableCell>
                  <TableCell>{agent.maxTickets || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleTogglePause(agent.id)}
                      title={agent.isPaused ? t('support:admin.agents.resume') : t('support:admin.agents.pause')}
                      size="small"
                    >
                      {agent.isPaused ? <ResumeIcon /> : <PauseIcon />}
                    </IconButton>
                    <IconButton onClick={() => handleRemove(agent.id)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add agent dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: 800 } }}
      >
        <DialogTitle>{t('support:admin.agents.add')}</DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', mt: 2 }}>
            <TextField
              fullWidth
              label={t('support:admin.agents.selectUser')}
              placeholder="Chercher par ID, email, nom..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!!selectedUser}
            />

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <Paper
                sx={{
                  mt: 1,
                  zIndex: 1000,
                  width: '100%',
                  maxHeight: 300,
                  overflow: 'auto',
                  position: 'relative'
                }}
              >
                <List sx={{ p: 0 }}>
                  {searchResults.map((user) => (
                    <ListItemButton
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      sx={{ p: 1.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar src={user.profilePicture} sx={{ mr: 1 }}>
                          {user.name?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            {searchLoading && (
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {searchQuery && searchResults.length === 0 && !searchLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('common:noResults')}
              </Typography>
            )}
          </Box>

          {/* Selected user display */}
          {selectedUser && (
            <Card sx={{ mt: 2, bgcolor: 'action.hover' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={selectedUser.profilePicture}>
                    {selectedUser.name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedUser.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>
                <Button size="small" onClick={handleClearSelection}>
                  {t('common:clear')}
                </Button>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common:cancel', 'Annuler')}</Button>
          <Button
            variant="contained"
            onClick={handleAddAgent}
            disabled={!selectedUser}
          >
            {t('support:admin.agents.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSupportAgents;
